import { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Flow, Item, List, Scenario } from '../../core/domain/models'
import { getRandomFlowColor, resolveFlowColor } from '../../core/domain/flowColors'
import { FormModal } from '../components/FormModal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Modal, ModalHeader, ModalBody } from '../components/Modal'
import { FlowColorPicker } from '../components/FlowColorPicker'
import { toastStore } from '../notifications/toast-store'
import {
  PencilIcon,
  PlusIcon,
} from '../components/Icons'

export interface ListModalsRef {
  openEditModal: () => void
  openDeleteModal: () => void
  openFlowsModal: () => void
}

export interface ListModalsProps {
  list: List | null
  items: Item[]
  flows: Flow[]
  selectedFlowId: string
  setSelectedFlowId: (flowId: string) => void
  canEdit: boolean
  scenario: Scenario | null
  scenarioId: string
  listId: string
  backToScenario: () => void
  command: any // useCommand return type
}

export const ListModals = forwardRef<ListModalsRef, ListModalsProps>(function ListModals(props, ref) {
  const {
    list,
    items,
    flows,
    selectedFlowId,
    setSelectedFlowId,
    canEdit,
    scenario,
    scenarioId,
    listId,
    backToScenario,
    command,
  } = props

  const navigate = useNavigate()

  // --- Edit modal state ---
  const [showEditModal, setShowEditModal] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // --- Delete modal state ---
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // --- Flows modal state ---
  const [showFlowsModal, setShowFlowsModal] = useState(false)
  const [newFlowName, setNewFlowName] = useState('')
  const [newFlowColor, setNewFlowColor] = useState<string>(getRandomFlowColor())

  useEffect(() => {
    if (showFlowsModal) {
      setNewFlowColor(getRandomFlowColor())
    }
  }, [showFlowsModal])

  // Escape key handler for modals
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showEditModal) setShowEditModal(false)
        if (showDeleteModal) setShowDeleteModal(false)
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [showEditModal, showDeleteModal])

  // Expose open triggers to parent via ref
  useImperativeHandle(ref, () => ({
    openEditModal: () => {
      setEditName(list?.name || '')
      setEditDescription(list?.description || '')
      setShowEditModal(true)
    },
    openDeleteModal: () => setShowDeleteModal(true),
    openFlowsModal: () => setShowFlowsModal(true),
  }), [list?.name, list?.description])

  // --- Handlers ---

  const handleUpdateList = async () => {
    if (!editName.trim()) return

    try {
      await command.renameList(
        scenarioId,
        listId,
        editName.trim(),
        editDescription.trim() || undefined,
        list?.name
      )
      setShowEditModal(false)
    } catch (error) {
      console.error('Error updating list:', error)
      toastStore.push({ message: 'Could not update list. Please try again.', type: 'error' })
    }
  }

  const handleDeleteList = async () => {
    try {
      await command.deleteList(scenarioId, listId, list?.name)
      backToScenario()
    } catch (error) {
      console.error('Error deleting list:', error)
      toastStore.push({ message: 'Could not delete list. Please try again.', type: 'error' })
    }
  }

  const handleCreateFlow = async () => {
    if (!newFlowName.trim() || !scenarioId) return

    try {
      const flowId = `flow_${crypto.randomUUID()}`
      await command.createFlow(
        scenarioId,
        flowId,
        newFlowName.trim(),
        undefined,
        newFlowColor
      )

      setNewFlowName('')
      setNewFlowColor(getRandomFlowColor())
      setShowFlowsModal(false)

      // Select the new flow
      setSelectedFlowId(flowId)
    } catch (error) {
      console.error('Error creating flow:', error)
      toastStore.push({ message: 'Could not create flow. Please try again.', type: 'error' })
    }
  }

  return (
    <>
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateList}
        title="Edit List"
        submitLabel="Save Changes"
        name={editName}
        onNameChange={setEditName}
        nameLabel="List Name"
        namePlaceholder="Enter list name"
        description={editDescription}
        onDescriptionChange={setEditDescription}
        descriptionLabel="Description"
        descriptionPlaceholder="Enter list description (optional)"
      />

      <Modal
        isOpen={showFlowsModal}
        onClose={() => {
          setShowFlowsModal(false)
          setNewFlowName('')
          setNewFlowColor(getRandomFlowColor())
        }}
        size="small"
      >
        <ModalHeader title="Flows" onClose={() => {
          setShowFlowsModal(false)
          setNewFlowName('')
          setNewFlowColor(getRandomFlowColor())
        }} />
        <ModalBody>
          <p className="flow-popup-hint">
            Flows let you group and order items for a specific trip.
          </p>

          {flows.length > 0 && (
            <div className="flow-popup-flow-list">
              {flows.map((f: any) => {
                const isDefault = scenario?.defaultFlowId === f.id
                return (
                  <div key={f.id} className="flow-popup-flow-row">
                    <span
                      className="flow-popup-flow-dot"
                      aria-hidden="true"
                      style={{ background: `var(--flow-accent-${resolveFlowColor(f)})` }}
                    />
                    <button
                      type="button"
                      className={`flow-popup-flow-name ${selectedFlowId === f.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedFlowId(f.id)
                        setShowFlowsModal(false)
                        setNewFlowName('')
                      }}
                      aria-pressed={selectedFlowId === f.id}
                    >
                      {f.name}
                      {isDefault && <span style={{ marginLeft: '8px', fontSize: '0.75em', opacity: 0.7 }}>★</span>}
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {canEdit && !isDefault && (
                        <button
                          type="button"
                          className="flow-popup-edit-btn"
                          title="Set as default"
                          onClick={async () => {
                            try {
                              await command.setDefaultFlow(scenarioId, f.id)
                            } catch (error) {
                              console.error('Error setting default flow:', error)
                            }
                          }}
                        >
                          ☆
                        </button>
                      )}
                      <button
                        type="button"
                        className="flow-popup-edit-btn"
                        title="Edit flow"
                        onClick={() => {
                          setShowFlowsModal(false)
                          setNewFlowName('')
                          navigate(`/scenario/${scenarioId}/flow/${f.id}`, {
                            state: { backTo: `/scenario/${scenarioId}/list/${listId}?flow=${f.id}` },
                          })
                        }}
                      >
                        <PencilIcon />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flow-popup-add-row">
            <span className="flow-popup-add-icon" aria-hidden="true">
              <PlusIcon />
            </span>
            <input
              className="flow-popup-add-input"
              type="text"
              value={newFlowName}
              onChange={(e) => setNewFlowName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFlowName.trim()) {
                  handleCreateFlow()
                }
              }}
              placeholder="New flow name..."
            />
          </div>

          <div className="flow-popup-color-row">
            <FlowColorPicker value={newFlowColor} onChange={setNewFlowColor} compact />
          </div>
        </ModalBody>
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteList}
        title="Delete List"
        message="Are you sure you want to delete this list?"
        warningMessage={`This will permanently delete the list and all ${items.length} item(s). This action cannot be undone.`}
        confirmLabel="Delete List"
        confirmVariant="danger"
      />
    </>
  )
})
